import type { ChatMessage, ReactionSummary } from "@codeshare/chat";
import { accentVar, isAccent } from "@codeshare/theme";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { ChatSession, ChatState } from "../chat/chat-session";

interface ChatPanelProps {
  state: ChatState;
  chat: ChatSession;
  selfClientId: string;
}

const time = (ts: number) =>
  new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
/** The reactions offered in the picker (any emoji received from a peer still shows). */
const REACTIONS = ["👍", "❤️", "😂", "🎉", "👀", "🚀"];

/** Re-send "typing" at most this often while the user types. */
const TYPING_REFRESH_MS = 2_000;

export function ChatPanel({ state, chat, selfClientId }: ChatPanelProps) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLOListElement>(null);
  const lastTyping = useRef(0);

  // Keep the newest message in view.
  useEffect(() => {
    const list = listRef.current;
    if (list && state.messages.length > 0) list.scrollTop = list.scrollHeight;
  }, [state.messages.length]);

  if (!state.active) {
    return (
      <p className="panel-hint">Chat works once you&apos;re in a room. Click Share to start one.</p>
    );
  }

  const send = (event?: FormEvent) => {
    event?.preventDefault();
    if (!draft.trim()) return;
    chat.send(draft);
    setDraft("");
    lastTyping.current = 0;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const onChange = (value: string) => {
    setDraft(value);
    const now = Date.now();
    if (value.trim() && now - lastTyping.current > TYPING_REFRESH_MS) {
      chat.setTyping(true);
      lastTyping.current = now;
    } else if (!value.trim() && lastTyping.current) {
      chat.setTyping(false);
      lastTyping.current = 0;
    }
  };

  return (
    <div className="chat">
      <ol className="chat-messages" ref={listRef} aria-label="Messages">
        {state.messages.length === 0 && <li className="panel-hint">No messages yet.</li>}
        {state.messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            own={message.clientId === selfClientId}
            reactions={state.reactions.get(message.id) ?? []}
            selfClientId={selfClientId}
            onReact={(emoji) => chat.react(message.id, emoji)}
          />
        ))}
      </ol>
      <p className="chat-typing" aria-live="polite">
        {state.typing.length === 1 && `${state.typing[0]?.name} is typing…`}
        {state.typing.length > 1 && `${state.typing.map((t) => t.name).join(", ")} are typing…`}
      </p>
      <form className="chat-composer" onSubmit={send}>
        <textarea
          aria-label="Message"
          placeholder="Message the room…"
          aria-describedby="chat-hint"
          rows={2}
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (lastTyping.current) chat.setTyping(false);
            lastTyping.current = 0;
          }}
        />
        <button type="submit" className="primary" disabled={!draft.trim()}>
          Send
        </button>
      </form>
      <p className="chat-hint" id="chat-hint">
        Enter to send · Shift+Enter for a new line
      </p>
    </div>
  );
}

interface MessageRowProps {
  message: ChatMessage;
  own: boolean;
  reactions: ReactionSummary[];
  selfClientId: string;
  onReact: (emoji: string) => void;
}

function MessageRow({ message, own, reactions, selfClientId, onReact }: MessageRowProps) {
  const color = isAccent(message.color) ? accentVar(message.color) : undefined;
  const [picking, setPicking] = useState(false);
  return (
    <li className={`chat-message${own ? " own" : ""}`}>
      <span className="chat-author" style={color ? { color } : undefined}>
        {own ? "You" : message.name}
      </span>
      <time className="chat-time" dateTime={new Date(message.ts).toISOString()}>
        {time(message.ts)}
      </time>
      {/* Plain text only (never HTML); newlines preserved by CSS. */}
      <p className="chat-text">{message.text}</p>
      <div className="chat-reactions">
        {reactions.map((group) => {
          const mine = group.people.some((p) => p.clientId === selfClientId);
          const names = group.people.map((p) => (p.clientId === selfClientId ? "You" : p.name));
          return (
            <button
              key={group.emoji}
              type="button"
              className="reaction"
              aria-pressed={mine}
              aria-label={`${group.emoji} ${group.people.length}: ${names.join(", ")}`}
              title={names.join(", ")}
              onClick={() => onReact(group.emoji)}
            >
              {group.emoji} {group.people.length}
            </button>
          );
        })}
        <button
          type="button"
          className="reaction reaction-add"
          aria-label="Add reaction"
          aria-expanded={picking}
          onClick={() => setPicking((p) => !p)}
        >
          ☺+
        </button>
        {picking && (
          <span className="reaction-picker">
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="reaction"
                aria-label={`React with ${emoji}`}
                onClick={() => {
                  onReact(emoji);
                  setPicking(false);
                }}
              >
                {emoji}
              </button>
            ))}
          </span>
        )}
      </div>
    </li>
  );
}
